import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowUpCircle, ArrowDownCircle, History, ChevronLeft, ChevronRight, Download, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Transaction type labels
const typeLabels = {
  depot: { label: 'Dépôt', isCredit: true },
  retrait: { label: 'Retrait', isCredit: false },
  bonus: { label: 'Bonus', isCredit: true },
  achat: { label: 'Achat', isCredit: false },
  vente: { label: 'Vente', isCredit: true },
  interet: { label: 'Intérêt', isCredit: true },
  mon_compte: { label: 'Mon compte', isCredit: true },
  trade: { label: 'Trade', isCredit: true },
  perte: { label: 'Perte', isCredit: false },
  frais: { label: 'Frais', isCredit: false },
  transfert: { label: 'Transfert', isCredit: false },
  transfert_credit: { label: 'Crédit transfert', isCredit: true },
  retrait_livret: { label: 'Retrait livret', isCredit: true },
  ajout_livret: { label: 'Ajout livret', isCredit: false },
  souscription: { label: 'Souscription', isCredit: false },
};

// Status labels
const statusLabels = {
  en_verification: 'En vérification',
  en_attente: 'En attente',
  a_payer: 'A payer',
  payee: 'Payée',
  valide: 'Validé',
  confirme: 'Confirmé',
  en_cours: 'En cours',
  cloture: 'Clôturé',
  vendu: 'Vendu',
  termine: 'Terminé',
  conteste: 'Contesté',
  annule: 'Annulé',
};

// Status colors
const getStatusColor = (status) => {
  const greenStatuses = ['valide', 'confirme', 'payee', 'termine'];
  const yellowStatuses = ['en_attente', 'en_verification', 'en_cours', 'a_payer'];
  const redStatuses = ['conteste', 'annule'];
  
  if (greenStatuses.includes(status)) return 'bg-green-500/20 text-green-500 border-green-500/50';
  if (yellowStatuses.includes(status)) return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50';
  if (redStatuses.includes(status)) return 'bg-red-500/20 text-red-500 border-red-500/50';
  return 'bg-secondary';
};

export default function ClientTransactions() {
  const { token, user } = useAuth();
  const { currentTemplate } = useTheme();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [transactions, setTransactions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositAgreement, setDepositAgreement] = useState(false);
  const [depositTerms, setDepositTerms] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Pagination and filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCredits, setTotalCredits] = useState(0);
  const [netBalance, setNetBalance] = useState(0);
  const [pendingDeposits, setPendingDeposits] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Trading portfolio
  const [tradingPortfolioEnabled, setTradingPortfolioEnabled] = useState(false);
  const [tradingPortfolio, setTradingPortfolio] = useState(null);
  
  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';
  const accentColor = currentTemplate?.colors?.accent || '#10B981';

  // Check URL params to open dialog automatically
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'deposit') {
      setDepositDialogOpen(true);
      // Clear the param after opening
      setSearchParams({});
    } else if (action === 'withdraw') {
      setWithdrawDialogOpen(true);
      // Clear the param after opening
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [token, user?.id, page, filterStatus, filterType, startDate, endDate]);

  const fetchData = async () => {
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 15);
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('tx_type', filterType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const [transactionsRes, requestsRes, tradingEnabledRes, tradingPortfolioRes] = await Promise.all([
        axios.get(`${API}/transactions/${user.id}?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { transactions: [], total_credits: 0, total_debits: 0, pending_deposits: 0, total_pages: 1 } })),
        axios.get(`${API}/funds/requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/portfolio/trading-enabled/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { enabled: false } })),
        axios.get(`${API}/portfolio/trading/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setTransactions(transactionsRes.data?.transactions || []);
      setTotalPages(transactionsRes.data?.total_pages || 1);
      setTotalCredits(transactionsRes.data?.total_credits || 0);
      setNetBalance(
        typeof transactionsRes.data?.net_balance === 'number'
          ? transactionsRes.data.net_balance
          : (transactionsRes.data?.total_credits || 0) - (transactionsRes.data?.total_debits || 0)
      );
      setPendingDeposits(transactionsRes.data?.pending_deposits || 0);
      setRequests(requestsRes.data || []);
      setTradingPortfolioEnabled(tradingEnabledRes.data?.enabled || false);
      setTradingPortfolio(tradingPortfolioRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('error'), { description: 'Montant invalide' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/funds/deposit`, {
        amount: parseFloat(amount),
        currency: 'EUR',
        description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('success'), { description: 'Demande de dépôt envoyée' });
      setDepositDialogOpen(false);
      setAmount('');
      setDescription('');
      fetchData();
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Erreur' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('error'), { description: 'Montant invalide' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/funds/withdraw`, {
        amount: parseFloat(amount),
        currency: 'EUR',
        description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('success'), { description: 'Demande de retrait envoyée' });
      setWithdrawDialogOpen(false);
      setAmount('');
      setDescription('');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur';
      if (detail.includes('KYC')) {
        toast.error('Retrait impossible', {
          description: 'Verification KYC incomplete. Fournissez vos documents.',
          action: { label: 'Mes Documents', onClick: () => { setWithdrawDialogOpen(false); navigate('/client/documents'); } },
          duration: 8000,
        });
      } else {
        toast.error(t('error'), { description: detail });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const clearFilters = () => {
    setFilterStatus('');
    setFilterType('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-xl sm:text-2xl font-bold">Mes Transactions</h1>
          <div className="flex gap-2">
            <Dialog open={depositDialogOpen} onOpenChange={(open) => { setDepositDialogOpen(open); if (open) { setDepositAgreement(false); setDepositTerms(false); } }}>
              <DialogTrigger asChild>
                <Button size="sm" style={{ backgroundColor: accentColor }} data-testid="deposit-btn">
                  <ArrowUpCircle className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm">Demander un dépôt</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Demande de dépôt</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Montant (€)</Label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      min={0}
                      data-testid="deposit-amount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description (optionnel)</Label>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Ex: Virement bancaire"
                    />
                  </div>
                  <div className="space-y-3 pt-2 border-t">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={depositAgreement} onChange={(e) => setDepositAgreement(e.target.checked)} className="mt-1" data-testid="deposit-agreement" />
                      <span className="text-sm">Bon pour accord</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={depositTerms} onChange={(e) => setDepositTerms(e.target.checked)} className="mt-1" data-testid="deposit-terms" />
                      <span className="text-sm">J&apos;accepte les Termes et Conditions</span>
                    </label>
                  </div>
                  <Button 
                    onClick={handleDeposit} 
                    disabled={submitting || !depositAgreement || !depositTerms}
                    className="w-full"
                    style={{ backgroundColor: accentColor }}
                    data-testid="confirm-deposit"
                  >
                    {submitting ? 'Envoi...' : 'Soumettre la demande'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="withdraw-btn">
                  <ArrowDownCircle className="w-4 h-4 mr-2" />
                  Demander un retrait
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Demande de retrait</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Montant (€)</Label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      min={0}
                      data-testid="withdraw-amount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description (optionnel)</Label>
                    <Input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Ex: Vers compte courant"
                    />
                  </div>
                  <Button 
                    onClick={handleWithdraw} 
                    disabled={submitting}
                    className="w-full"
                    style={{ backgroundColor: primaryColor }}
                    data-testid="confirm-withdraw"
                  >
                    {submitting ? 'Envoi...' : 'Soumettre la demande'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-card/80 backdrop-blur">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Solde net</p>
              <p className="text-2xl font-bold" style={{ color: accentColor }} data-testid="client-transactions-net-balance">
                {netBalance.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
              </p>
              {pendingDeposits > 0 && (
                <p className="text-sm text-yellow-500 mt-1">
                  À venir: +{pendingDeposits.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € (dépôt)
                </p>
              )}
            </CardContent>
          </Card>
          {tradingPortfolioEnabled && (
            <Card className="bg-card/80 backdrop-blur border-purple-500/30">
              <CardContent className="p-4">
                <p className="text-sm text-purple-400">Portefeuille Trading</p>
                <p className="text-2xl font-bold text-purple-500" data-testid="client-history-trading-balance">
                  {(tradingPortfolio?.available || tradingPortfolio?.balance || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Filters */}
        <Card className="bg-card/80 backdrop-blur">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Date début</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date fin</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {Object.entries(typeLabels).map(([value, { label }]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Statut</Label>
                <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={clearFilters} size="sm">
                Réinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transactions List */}
        <Card className="bg-card/80 backdrop-blur">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historique des transactions
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/exports/transactions/${user?.id}`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob'
                    });
                    const url = URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Transactions.xlsx';
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch { toast.error('Erreur export'); }
                }}
                data-testid="export-transactions-excel"
              >
                <Download className="w-4 h-4 mr-1" />
                Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/exports/statement/${user?.id}`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob'
                    });
                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    window.open(url, '_blank');
                  } catch { toast.error('Erreur export'); }
                }}
                data-testid="export-statement-pdf"
              >
                <FileText className="w-4 h-4 mr-1" />
                Relevé PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Aucune transaction trouvée</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Date</th>
                        <th className="text-left py-3 px-2">Description</th>
                        <th className="text-left py-3 px-2">Type</th>
                        <th className="text-right py-3 px-2">Montant</th>
                        <th className="text-center py-3 px-2">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => {
                        const baseTypeInfo = typeLabels[tx.transaction_type] || { label: tx.transaction_type, isCredit: true };
                        // For transfers, determine credit/debit based on direction
                        const isTransferIn = tx.transaction_type === 'transfert' && 
                          ['to_available', 'trading_to_available'].includes(tx.transfer_direction);
                        const typeInfo = tx.transaction_type === 'transfert' 
                          ? { ...baseTypeInfo, isCredit: isTransferIn }
                          : baseTypeInfo;
                        // Format date properly
                        const formatDate = (dateStr) => {
                          if (!dateStr) return '-';
                          try {
                            const date = new Date(dateStr);
                            return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                          } catch {
                            return dateStr;
                          }
                        };
                        return (
                          <tr key={tx.id} className="border-b border-border/50 hover:bg-secondary/30">
                            <td className="py-3 px-2 text-muted-foreground">{formatDate(tx.transaction_date || tx.created_at)}</td>
                            <td className="py-3 px-2 font-medium">{tx.description}</td>
                            <td className="py-3 px-2">
                              <Badge variant="outline" className={typeInfo.isCredit ? 'text-green-500 border-green-500/50' : 'text-red-500 border-red-500/50'}>
                                {typeInfo.label}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-right font-mono" style={{ color: typeInfo.isCredit ? accentColor : '#ef5350' }}>
                              {typeInfo.isCredit ? '+' : '-'}{tx.amount?.toLocaleString()} {tx.currency}
                            </td>
                            <td className="py-3 px-2 text-center">
                              <Badge variant="outline" className={getStatusColor(tx.status)}>
                                {statusLabels[tx.status] || tx.status}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {page} sur {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
}
